import type { SearchSelectItem } from '@/components/UI/SearchSelect';
import type { AlphaAtlasReportFilters } from './AlphaAtlasReportState';
import { humanizeCatalogLabel } from './alphaAtlasFormat';

export type AlphaAtlasCatalogMember = AlphaAtlasReportFilters['parents'][number];

const DURATION_SEGMENT = /^\d+(?:m|h|d|w|mo)$/i;

export const catalogMemberSearchText = (semanticKey: string, displayName: string): string => {
    const segments = semanticKey.split('.').filter(Boolean);
    const durationIndependent = segments.filter((segment) => !DURATION_SEGMENT.test(segment));
    const numericValue = durationIndependent.at(-1);
    const aliases = [
        semanticKey,
        semanticKey.replace(/[._-]+/g, ' '),
        durationIndependent.join(' '),
        displayName,
        displayName.replace(/[^\p{L}\p{N}]+/gu, ' '),
        ...(numericValue && /^\d+$/.test(numericValue) && durationIndependent[0] ? [`${durationIndependent[0]} id ${numericValue}`] : [])
    ];
    return [...new Set(aliases.map((alias) => alias.trim().toLowerCase()).filter(Boolean))].join(' ');
};

/**
 * SearchSelect is number-keyed; catalog members (keyed by fingerprint) use their 1-based position.
 * Pair with `catalogIndexByFingerprint` to map the selected number back.
 */
export const catalogSelectItems = (members: AlphaAtlasCatalogMember[]): SearchSelectItem[] =>
    members.map((member, index) => {
        const name = humanizeCatalogLabel(member.label);
        return { value: index + 1, label: name, name, meta: member.label, searchText: catalogMemberSearchText(member.label, name) };
    });

export const catalogIndexByFingerprint = (members: AlphaAtlasCatalogMember[]): Map<string, number> =>
    new Map(members.map((member, index) => [member.fingerprint, index + 1]));

export const entitySelectItems = (entities: AlphaAtlasReportFilters['entities']): SearchSelectItem[] =>
    entities.map((entity, index) => ({
        value: index + 1,
        displayId: entity.entityId,
        label: entity.label,
        name: entity.label,
        badge: entity.entityType === 'AssetIndex' ? 'Index' : 'Stock',
        badgeTone: 'neutral',
        searchText: entity.entityType === 'AssetIndex' ? 'index' : 'stock'
    }));
